import { useRef, useState } from "react";
import { useSocialMediaPost } from "../api/getPost";
import { Box, Button, Dialog, DialogActions } from "@mui/material";

import { ImageFieldGallery } from "../../../ui/components/Form/ImageField";
import { useDeletePostImage } from "../api/deletePostImage";
import { useInsertPostAttachment } from "../api/insertPostAttachment";
import { nanoid } from "nanoid";
import { useQueryClient } from "@tanstack/react-query";

export function EditPostImages({ postData, children }) {
  const fileInputRef = useRef();
  const postId = postData?.postagemid;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [images, setImages] = useState([]);
  const queryClient = useQueryClient();
  useSocialMediaPost({
    params: {
      postagemid: postId,
      temaid: postData?.temaid,
    },
    config: {
      enabled: isDialogOpen,
      onSuccess: (data) => {
        setImages(data.carrossel.split(",").map((src) => ({ src })));
      },
    },
  });
  const deletePostImage = useDeletePostImage();
  const insertPostAttachment = useInsertPostAttachment();

  const openDialog = () => {
    setIsDialogOpen(true);
  };
  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleChange = (e) => {
    const file = e.target.files[0];

    setImages((prev) => {
      const newValue = prev;
      newValue.push({
        src: URL.createObjectURL(file),
        added: true,
        file,
      });
      return [...newValue];
    });
  };

  const imageSrcs = images
    .filter(({ deleted, ...rest }) => {
      return !deleted;
    })
    .map(({ src }) => src);

  const handleDeleteImage = (filteredArrIndex) => {
    setImages((prev) => {
      const newArr = structuredClone(prev);
      const src = imageSrcs[filteredArrIndex];
      const index = newArr.findIndex((foo) => foo.src === src);
      const obj = prev[index];
      if (obj.added) {
        newArr.splice(index, 1);
        return [...newArr];
      }

      obj.deleted = true;
      newArr[index] = obj;
      return [...newArr];
    });
  };

  const saveChanges = () => {
    images.forEach(async (image, index) => {
      if (image.deleted) {
        await deletePostImage.mutate(
          { postId, imageURL: image.src },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ["socialMediaFeed"] });
            },
          }
        );
      }
      if (image.added) {
        const rgx = new RegExp("[^a-zA-Z0-9 -]");
        const id = nanoid().replace(rgx, "");
        await insertPostAttachment.mutate(
          {
            postId,
            file: image.file,
            title: `ANEXO_POST_ALUNO_${postId}_${id}`,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ["socialMediaFeed"] });
            },
          }
        );
      }
      if (images.length === index + 1) {
        closeDialog();
      }
    });
  };

  return (
    <>
      {children({ openDialog, closeDialog })}
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        maxWidth={`sm`}
        fullWidth
      >
        <Box
          sx={{
            aspectRatio: postData.proporcaoImagem,
            width: "100%",
            height: "max-content",
          }}
        >
          <ImageFieldGallery
            imageSrcs={imageSrcs}
            onDeleteItem={handleDeleteImage}
            fileInputRef={fileInputRef}
            canDeleteImage={() => {
              return imageSrcs.length > 1;
            }}
          />
        </Box>

        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button onClick={saveChanges}>Salvar</Button>
        </DialogActions>
        <input
          id="image_upload_input"
          ref={fileInputRef}
          onChange={handleChange}
          multiple={true}
          type="file"
          accept="image/*"
          hidden
        />
      </Dialog>
    </>
  );
}
